import { z } from 'zod';
import type { RefinementCtx, ZodEffects, ZodIntersection, ZodObject, ZodRawShape } from 'zod';
import { friendlyRole } from 'helpers/domain-specific/role.helpers';
import { joinWithConjunction } from 'helpers/string.helpers';
import { furtherEscalationRoles } from './active-reviewer.helpers';

const activeReviewerRequiredMessage = 'Please select an active reviewer.';

const isSameActiveReviewerMessage =
  'The same active reviewer is selected, please select a different reviewer.';

const furtherEscalatedMessage = `${joinWithConjunction(
  furtherEscalationRoles.map((role) => friendlyRole(role)).filter((role) => role !== undefined),
  'or'
)} to review the mark further.`;

const invalidAdminMessage = `Please select a ${furtherEscalatedMessage}`;

const isSameAdminSelectedMessage = `Please select a different ${furtherEscalatedMessage}`;

const unrefinedSetActiveReviewerFormSchema = z.object({
  organisationUserId: z.number().nullable(),
  furtherEscalated: z.boolean()
});

export type SetActiveReviewerFormValues = Required<
  z.infer<typeof unrefinedSetActiveReviewerFormSchema>
>;

function organisationUserIdSchema(
  initialOrganisationUserId: number | null
): ZodEffects<ZodObject<ZodRawShape>> {
  return unrefinedSetActiveReviewerFormSchema.superRefine(
    (formData: SetActiveReviewerFormValues, ctx: RefinementCtx) => {
      // Ensure that an organisation user is specified when further escalated is toggled.
      // This can occur in cases where there was an existing reviewer who had a marking admin role only.
      // In this case, the active reviewer gets cleared, so we want to show the user an error message.
      if (!formData.organisationUserId && formData.furtherEscalated) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: invalidAdminMessage,
          path: ['organisationUserId']
        });
      }

      // Ensure that an organisation user ID for the new active reviewer is specified on
      // save, even if an ID wasn't loaded. This is so that we can display an
      // error message if the user doesn't select an active reviewer when the form
      // is submitted.
      if (!initialOrganisationUserId && !formData.organisationUserId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: activeReviewerRequiredMessage,
          path: ['organisationUserId']
        });
      }
    }
  );
}

function getSameActiveReviewerSchema(
  initialOrganisationUserId: number
): ZodEffects<ZodObject<ZodRawShape>> {
  return unrefinedSetActiveReviewerFormSchema.superRefine(
    (formData: SetActiveReviewerFormValues, ctx: RefinementCtx) => {
      const isSameActiveReviewer = formData.organisationUserId === initialOrganisationUserId;
      if (isSameActiveReviewer && !formData.furtherEscalated) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: isSameActiveReviewerMessage,
          path: ['organisationUserId']
        });
      }

      if (isSameActiveReviewer && formData.furtherEscalated)
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: isSameAdminSelectedMessage,
          path: ['organisationUserId']
        });
    }
  );
}

type SchemaForZodIntersection = ZodEffects<ZodObject<ZodRawShape>>;

export function setActiveReviewerFormSchema(
  organisationUserId: number | null
):
  | ZodIntersection<SchemaForZodIntersection, SchemaForZodIntersection>
  | ZodEffects<ZodObject<ZodRawShape>> {
  if (organisationUserId) {
    return z.intersection(
      getSameActiveReviewerSchema(organisationUserId),
      organisationUserIdSchema(organisationUserId)
    );
  }

  return organisationUserIdSchema(organisationUserId);
}
